import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { isLoadedSnapshot, useDialPacing } from '../pacing';
import { IconChevronRight, IconX } from './icons';

export type TopBarProps = {
	onNextLead: () => void;
	onClose: () => void;
	/**
	 * Optional "Skip lead" affordance. When provided, the TopBar renders a
	 * subdued button between the pacing strip and "Next lead" so reps can skip
	 * a queue entry without dialing first (legacy queue's `tempSkipReasons` /
	 * `skipReasons` modal entry-point). Hidden when undefined so existing
	 * tests that don't supply it don't have to render the button.
	 */
	onSkipLead?: () => void;
	/** Disabled while no lead is loaded or while a skip mutation is in-flight. */
	skipLeadDisabled?: boolean;
};

/**
 * Slim daily-dial progress chip rendered inline with the TopBar actions. The
 * floating pacing pill is gone (milestone Dialog handles celebration); reps
 * still want a passive read on progress, so this chip surfaces dials-vs-goal
 * with a thin underline-style progress bar. Skeleton + no-goal cases collapse
 * gracefully.
 */
const TopBarPacingChip: FC = () => {
	const { snapshot } = useDialPacing();
	if (!isLoadedSnapshot(snapshot)) {
		return <span className={css(styles.pacingChipSkeleton)} />;
	}
	const { dialsToday, dialGoal } = snapshot;
	const label = dialGoal != null ? `${dialsToday} / ${dialGoal} dials` : `${dialsToday} dials`;
	const pct = dialGoal != null ? Math.max(0, Math.min(100, (dialsToday / Math.max(1, dialGoal)) * 100)) : 0;
	return (
		<div className={css(styles.pacingChip)} title='Daily dial pacing'>
			<span className={css(styles.pacingChipLabel)}>{label}</span>
			{dialGoal != null && (
				<span
					className={css(styles.pacingChipTrack)}
					role='progressbar'
					aria-label='Daily dial progress'
					aria-valuenow={dialsToday}
					aria-valuemin={0}
					aria-valuemax={dialGoal}
				>
					<span className={css(styles.pacingChipFill)} style={{ width: `${pct}%` }} />
				</span>
			)}
		</div>
	);
};

export const TopBar: FC<TopBarProps> = ({ onNextLead, onClose, onSkipLead, skipLeadDisabled }) => {
	return (
		<div className={css(styles.bar)}>
			<div className={css(styles.actions)}>
				<TopBarPacingChip />
				<div className={css(styles.divider)} />
				{onSkipLead && (
					<>
						<button
							type='button'
							onClick={onSkipLead}
							disabled={skipLeadDisabled}
							title='Skip this lead with a reason'
							className={css(styles.skipBtn, skipLeadDisabled && styles.skipBtnDisabled)}
						>
							Skip lead
						</button>
						<div className={css(styles.divider)} />
					</>
				)}
				<button type='button' onClick={onNextLead} title='Advance to the next lead' className={css(styles.nextBtn)}>
					Next lead
					<IconChevronRight size={13} sw={2.2} />
				</button>
				<div className={css(styles.divider)} />
				<button
					type='button'
					onClick={onClose}
					title='Exit focused view'
					aria-label='Exit focused view'
					className={css(styles.closeBtn)}
				>
					<IconX size={14} />
				</button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	// Inline header bar that lives at the top of the CENTER column. Matches the
	// LEFT column's `colHead` height (52px) so the LEAD label + globe + kebab
	// (rendered inside LeftColumn) share the same Y-row as the actions here
	// without overlapping like the previous fixed/floating pill did.
	bar: {
		height: 52,
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		padding: '0 16px',
		borderBottom: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		borderRadius: 999,
		padding: 4,
		boxShadow: 'var(--shadow-sm)',
	},
	nextBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 28,
		padding: '0 12px 0 10px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'var(--ink)',
		color: 'var(--bg)',
		border: 'none',
		cursor: 'pointer',
		fontFamily: 'inherit',
	},
	// Skip-lead is a secondary action — same height as Next lead but a quieter
	// outline treatment so the primary forward path stays visually dominant.
	skipBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		height: 28,
		padding: '0 12px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'transparent',
		color: 'var(--ink)',
		border: '1px solid var(--border)',
		cursor: 'pointer',
		fontFamily: 'inherit',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
		},
	},
	skipBtnDisabled: {
		opacity: 0.5,
		cursor: 'not-allowed',
		':hover': {
			background: 'transparent',
		},
	},
	divider: {
		width: 1,
		height: 18,
		background: 'var(--border)',
		margin: '0 2px',
	},
	closeBtn: {
		width: 28,
		height: 28,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 999,
		background: 'transparent',
		color: 'var(--ink-muted)',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	pacingChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		height: 28,
		padding: '0 12px',
		borderRadius: 999,
	},
	pacingChipLabel: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink)',
		whiteSpace: 'nowrap',
	},
	pacingChipTrack: {
		display: 'inline-block',
		position: 'relative',
		width: 64,
		height: 4,
		borderRadius: 2,
		background: 'var(--border)',
		overflow: 'hidden',
	},
	pacingChipFill: {
		display: 'block',
		height: '100%',
		background: 'var(--accent)',
		transition: 'width 200ms ease-out',
	},
	pacingChipSkeleton: {
		display: 'inline-block',
		width: 96,
		height: 12,
		borderRadius: 6,
		background: 'var(--border)',
		opacity: 0.55,
		marginRight: 4,
	},
});
